import { enforcementEnvironmentFor } from "@/api/mock-data/agent-estate/enforcementReadiness";
import {
  TOTAL_AGENT_COUNT,
  getTotalAgentCount,
} from "@/api/mock-data/agent-estate/generate";
import { DISCOVERY_DURATION_MS } from "@/api/mock-data/agent-estate/phases";
import {
  getMcpServerCatalog,
  getMcpToolCount,
  MCP_SERVER_CATALOG,
  MCP_TOOL_COUNT,
} from "@/api/mock-data/agent-estate/seed";
import { agentEstateStore } from "@/api/mock-data/agent-estate/store";
import { snapshotAccounts } from "@/api/mock-data/investment-banking/snapshot";
import { EstateReadiness } from "@/api/temp/TrustAIExecutiveService";
import { isInvestmentBankingDemo } from "@/libs/investmentBankingDemo";
import { ESTATE_COUNTS } from "./estate";

/**
 * The five readiness scenarios the Security Overview dashboard has to render,
 * and the clocks that make the two in-flight ones move.
 *
 * Like `agent-estate/phases`, nothing here ticks and nothing is written: a
 * RUNNING scenario is a pure function of wall-clock time since its start
 * stamp, so every read in a render pass derives the same snapshot and the
 * progress bar, the counts and the section gating can never disagree.
 */

/** The scenario names, which double as the `?dashboardState=` vocabulary. */
export const LIFECYCLE_SCENARIOS = [
  "ZERO_DAY",
  "DISCOVERING",
  "DISCOVERED",
  "EVALUATING",
  "ACTIVE",
] as const;

export type LifecycleScenario = (typeof LIFECYCLE_SCENARIOS)[number];

/**
 * Wall-clock budget for one evaluation run. Sibling to
 * `DISCOVERY_DURATION_MS`, and longer on purpose: evaluation walks every
 * discovered access path, so a run that finished as fast as discovery would
 * undersell what the state is for.
 */
export const EVALUATION_DURATION_MS = 30_000;

/**
 * The estate a completed discovery finds. Scenario C in the brief, and the
 * ceiling every earlier scenario counts up towards — DISCOVERING is a
 * partial view of this estate, not a different one.
 */
const DISCOVERED_ESTATE = {
  discoveredAgents: TOTAL_AGENT_COUNT,
  discoveredMcpServers: MCP_SERVER_CATALOG.length,
  discoveredTools: MCP_TOOL_COUNT,
  discoveredDataSources: ESTATE_COUNTS["data-sources"],
} as const;

/** Mid-run discovery snapshot: ~56% of the discovered ceiling above. */
const DISCOVERING_AGENT_COUNT = Math.round(TOTAL_AGENT_COUNT * 0.56);

/**
 * What discovery has reached at the point DISCOVERING is parked.
 *
 * Agents and data sources are in; MCP servers and tools are still at zero.
 * That asymmetry is the whole point of the state: a reader has to be able to
 * tell "we found none" from "we have not looked yet", and a screen where every
 * count is mid-way never exercises that distinction.
 */
const DISCOVERING_ESTATE = {
  discoveredAgents: DISCOVERING_AGENT_COUNT,
  discoveredMcpServers: 0,
  discoveredTools: 0,
  discoveredDataSources: ESTATE_COUNTS["data-sources"],
} as const;

const ZERO_ESTATE = {
  discoveredAgents: 0,
  discoveredMcpServers: 0,
  discoveredTools: 0,
  discoveredDataSources: 0,
} as const;

const clamp01 = (value: number) => Math.min(1, Math.max(0, value));

/** Elapsed fraction of `durationMs` since `startedAt`, or 0 with no stamp. */
const elapsedRatio = (startedAt: string | null, durationMs: number): number => {
  if (!startedAt) return 0;
  const started = new Date(startedAt).getTime();
  if (Number.isNaN(started)) return 0;
  return clamp01((Date.now() - started) / durationMs);
};

const percent = (ratio: number) => Math.round(ratio * 100);

/**
 * How the combined onboarding run splits between its two phases, matching the
 * two duration budgets. Derived rather than written down, so changing either
 * duration rescales the blended progress instead of desyncing it.
 */
const DISCOVERY_WEIGHT =
  DISCOVERY_DURATION_MS / (DISCOVERY_DURATION_MS + EVALUATION_DURATION_MS);

/**
 * A scenario's own fields, before the whole-run onboarding view is derived from
 * them. Keeping the fixtures to this shape means the four derived fields have
 * exactly one construction site and can never be spelled inconsistently across
 * five literals.
 */
type BaseReadiness = Omit<
  EstateReadiness,
  | "totalAgents"
  | "onboardingPercentComplete"
  | "agentsAnalyzed"
  | "chainedOnboarding"
>;

/** The base fixture for each scenario, before any clock is applied. */
const FIXTURES: Record<LifecycleScenario, BaseReadiness> = {
  // Scenario A — nothing connected. Every count is zero AND every status is
  // NOT_STARTED, which is what tells the UI those zeros are not estate counts.
  ZERO_DAY: {
    connectedPlatforms: 0,
    ...ZERO_ESTATE,
    discoveryStatus: "NOT_STARTED",
    discoveryPercentComplete: 0,
    evaluationStatus: "NOT_STARTED",
    evaluationPercentComplete: 0,
    agentsEvaluated: 0,
    dataSourcesAnalyzed: 0,
    completedEvaluations: 0,
  },
  // Scenario B — discovery in flight against two connected platforms.
  DISCOVERING: {
    connectedPlatforms: 2,
    ...DISCOVERING_ESTATE,
    discoveryStatus: "RUNNING",
    // Mid-run, matching the counts above against the discovered ceiling
    // (DISCOVERING_AGENT_COUNT of TOTAL_AGENT_COUNT agents). A fixture parked
    // at 0% would render every metric as "not discovered yet" and lose the
    // one distinction this state exists to demonstrate.
    discoveryPercentComplete: 56,
    evaluationStatus: "NOT_STARTED",
    evaluationPercentComplete: 0,
    agentsEvaluated: 0,
    dataSourcesAnalyzed: 0,
    completedEvaluations: 0,
  },
  // Scenario C — the estate is known, nothing has been judged about it yet.
  DISCOVERED: {
    connectedPlatforms: 2,
    ...DISCOVERED_ESTATE,
    discoveryStatus: "COMPLETED",
    discoveryPercentComplete: 100,
    evaluationStatus: "NOT_STARTED",
    evaluationPercentComplete: 0,
    agentsEvaluated: 0,
    dataSourcesAnalyzed: 0,
    completedEvaluations: 0,
  },
  // Scenario D — evaluation in flight over the full discovered estate.
  EVALUATING: {
    connectedPlatforms: 2,
    ...DISCOVERED_ESTATE,
    discoveryStatus: "COMPLETED",
    discoveryPercentComplete: 100,
    evaluationStatus: "RUNNING",
    evaluationPercentComplete: 56,
    agentsEvaluated: DISCOVERING_AGENT_COUNT,
    dataSourcesAnalyzed: DISCOVERED_ESTATE.discoveredDataSources,
    completedEvaluations: 0,
  },
  // Scenario E — today's dashboard. `completedEvaluations` is 2 rather than 1
  // so the seeded What's Changed section has the prior run it diffs against.
  ACTIVE: {
    connectedPlatforms: 2,
    ...DISCOVERED_ESTATE,
    discoveryStatus: "COMPLETED",
    discoveryPercentComplete: 100,
    evaluationStatus: "COMPLETED",
    evaluationPercentComplete: 100,
    agentsEvaluated: DISCOVERED_ESTATE.discoveredAgents,
    dataSourcesAnalyzed: DISCOVERED_ESTATE.discoveredDataSources,
    completedEvaluations: 2,
  },
};

/** Narrows an arbitrary string to a scenario, or returns null. */
export const asLifecycleScenario = (
  value: string | null | undefined,
): LifecycleScenario | null =>
  LIFECYCLE_SCENARIOS.includes(value as LifecycleScenario)
    ? (value as LifecycleScenario)
    : null;

interface ReadinessClocks {
  /** When this session's discovery run began, if one is in flight. */
  discoveryStartedAt: string | null;
  /** When this session's evaluation run began, if one is in flight. */
  evaluationStartedAt: string | null;
  /** Evaluation runs already banked, which the fixture's own value floors. */
  completedEvaluations: number;
  /**
   * Whether discovery completing should carry straight on into evaluation.
   *
   * Set by the onboarding wizard's Start Onboarding, which commits to one
   * continuous run. A platform connected from the catalog panel leaves this
   * false and still parks at DISCOVERED for an explicit "Start evaluation".
   */
  autoChainEvaluation: boolean;
}

/**
 * The whole-run view of a scenario: one denominator, one percentage, one count.
 *
 * Discovery and evaluation are two phases of one run to the reader, so they get
 * one bar. Blending the two percentages — rather than switching from one to the
 * other at the seam — is what stops the agent count falling back to zero
 * halfway through.
 */
const withOnboardingFields = (
  base: BaseReadiness,
  chained: boolean,
  /** The agents actually onboarded, when a live environment says so. */
  importedAgents?: number,
): EstateReadiness => {
  // Nothing connected means there is no estate to count towards, and "0 of 0"
  // would read as a finished run rather than one that has not begun.
  const totalAgents =
    base.discoveryStatus === "NOT_STARTED" && base.connectedPlatforms === 0
      ? 0
      : (importedAgents ?? DISCOVERED_ESTATE.discoveredAgents);
  const onboardingPercentComplete = Math.round(
    base.discoveryPercentComplete * DISCOVERY_WEIGHT +
      base.evaluationPercentComplete * (1 - DISCOVERY_WEIGHT),
  );

  return {
    ...base,
    totalAgents,
    onboardingPercentComplete,
    agentsAnalyzed: Math.round((totalAgents * onboardingPercentComplete) / 100),
    chainedOnboarding: chained,
  };
};

/** When a chained evaluation begins: the instant its discovery run finished. */
const chainedEvaluationStart = (discoveryStartedAt: string): string =>
  new Date(
    new Date(discoveryStartedAt).getTime() + DISCOVERY_DURATION_MS,
  ).toISOString();

/**
 * An evaluation run in flight, or the ACTIVE estate it lands on.
 *
 * Shared by the EVALUATING scenario and by the chained tail of a DISCOVERING
 * one, so both halves of a continuous run are described by the same arithmetic.
 */
const evaluatingSnapshot = (
  startedAt: string,
  bankedEvaluations: number,
): BaseReadiness => {
  const fixture = FIXTURES.EVALUATING;
  const ratio = elapsedRatio(startedAt, EVALUATION_DURATION_MS);

  if (ratio >= 1) {
    return {
      ...FIXTURES.ACTIVE,
      // One more run than was banked before this one finished — which is how
      // a first evaluation stays at 1 and keeps What's Changed hidden.
      completedEvaluations: bankedEvaluations + 1,
    };
  }

  return {
    ...fixture,
    evaluationPercentComplete: percent(ratio),
    agentsEvaluated: Math.round(fixture.discoveredAgents * ratio),
    dataSourcesAnalyzed: Math.round(fixture.discoveredDataSources * ratio),
    completedEvaluations: Math.max(
      fixture.completedEvaluations,
      bankedEvaluations,
    ),
  };
};

/**
 * The readiness snapshot for `scenario` at this instant.
 *
 * A clock only applies to a run *this session started* — `startOnboardingRun`
 * and `startEvaluation` stamp one, and the counts then
 * grow from zero as the run progresses, which is what makes the in-session
 * transition watchable.
 *
 * Without a stamp the fixture is served verbatim. That is what a state parked
 * by `setScenario` or by `?dashboardState=` should show: a deterministic
 * mid-run snapshot. Deriving it from a clock that was never started would peg
 * the ratio at 0, rendering every count as "not discovered yet" and making the
 * state indistinguishable from the one before it.
 */
export const readinessFor = (
  scenario: LifecycleScenario,
  clocks: ReadinessClocks,
): EstateReadiness => {
  const fixture = FIXTURES[scenario];
  const completedEvaluations = Math.max(
    fixture.completedEvaluations,
    clocks.completedEvaluations,
  );

  // One `withOnboardingFields` call, so no future branch can return a snapshot
  // that skipped it.
  const base = ((): BaseReadiness => {
    if (fixture.discoveryStatus === "RUNNING") {
      if (!clocks.discoveryStartedAt) return { ...fixture, completedEvaluations };
      const ratio = elapsedRatio(
        clocks.discoveryStartedAt,
        DISCOVERY_DURATION_MS,
      );

      if (ratio >= 1) {
        // A chained run carries on into evaluation on a clock derived from the
        // same stamp - nothing further is written, and the reader is never
        // asked to start the second half of a run they already committed to.
        if (clocks.autoChainEvaluation)
          return evaluatingSnapshot(
            chainedEvaluationStart(clocks.discoveryStartedAt),
            clocks.completedEvaluations,
          );
        // Otherwise discovery completing hands off to DISCOVERED rather than
        // staying parked at 100%: the reader's next question is "now evaluate
        // it", and a finished bar is not that.
        return { ...FIXTURES.DISCOVERED, completedEvaluations };
      }

      return {
        ...fixture,
        // Counts grow with the run, so the estate fills in as it is found.
        discoveredAgents: Math.round(fixture.discoveredAgents * ratio),
        discoveredDataSources: Math.round(
          fixture.discoveredDataSources * ratio,
        ),
        discoveryPercentComplete: percent(ratio),
        completedEvaluations,
      };
    }

    if (fixture.evaluationStatus === "RUNNING") {
      if (!clocks.evaluationStartedAt)
        return { ...fixture, completedEvaluations };
      return evaluatingSnapshot(
        clocks.evaluationStartedAt,
        clocks.completedEvaluations,
      );
    }

    return { ...fixture, completedEvaluations };
  })();

  const started = base.discoveryStatus !== "NOT_STARTED";
  const environment = agentEstateStore.listEnvironments().at(-1);
  // The wizard imports a selection, not the whole registry: count what was
  // actually onboarded, so the board and the estate agree on the total.
  const readiness = withOnboardingFields(
    base,
    clocks.autoChainEvaluation,
    started ? environment?.totalAgents : undefined,
  );
  const progress =
    started && environment
      ? agentEstateStore.getProgress(environment.id)
      : undefined;
  const enforcement = {
    enforcement: progress?.enforcement,
    enforcementEnvironment: enforcementEnvironmentFor(),
  };
  if (!isInvestmentBankingDemo()) return { ...readiness, ...enforcement };
  return {
    ...readiness,
    connectedPlatforms: started && environment ? 1 : 0,
    totalAgents: started ? getTotalAgentCount() : 0,
    discoveredAgents: progress?.discoveredCount ?? 0,
    agentsEvaluated: progress?.classifiedCount ?? 0,
    agentsAnalyzed: progress?.classifiedCount ?? 0,
    discoveredDataSources: snapshotAccounts.length,
    dataSourcesAnalyzed: 0,
    discoveredMcpServers: started ? getMcpServerCatalog().length : 0,
    discoveredTools: started ? getMcpToolCount() : 0,
    // Already tallied by the progress snapshot above - one pass, not two.
    ...enforcement,
  };
};
